/**
 * Mesa por imprimir: tarjeta con copias e «Imprimir QR» a la vista
 */

import { useState } from 'react';
import { Printer } from 'lucide-react';
import type { DeskOrder } from '@/services/deskService';
import { QuantityStepper } from './QuantityStepper';
import { defaultCopies, hasPhotos, orderTime, tableName } from './orderDisplay';

interface PendingTableCardProps {
  order: DeskOrder;
  isPrinting: boolean;
  /** Propietario o gerente: puede añadir fotos en Menú */
  canManageItems: boolean;
  onPrint: (copies: number) => void;
}

export function PendingTableCard({ order, isPrinting, canManageItems, onPrint }: PendingTableCardProps) {
  const [copies, setCopies] = useState(() => defaultCopies(order));
  const guests = order.identification.guests_count ?? 0;

  if (!hasPhotos(order)) {
    return (
      <article className="grid min-w-0 content-start gap-3 rounded border border-dashed border-gray-300 bg-white p-4">
        <Heading name={tableName(order)} time={orderTime(order)} />
        <p className="flex items-center gap-2 text-sm text-content-secondary">
          <span className="h-2 w-2 shrink-0 rounded-full bg-gray-300" aria-hidden />
          Sin fotos
        </p>
        <p className="text-sm font-light leading-snug text-gray-600">
          {canManageItems
            ? 'Ningún item de esta mesa tiene foto. Añádela en Menú y la mesa se podrá imprimir.'
            : 'Ningún item de esta mesa tiene foto. Avisa a un gerente.'}
        </p>
      </article>
    );
  }

  return (
    <article className="grid min-w-0 content-start gap-3 rounded border border-amber-200 bg-amber-50 p-4">
      <Heading name={tableName(order)} time={orderTime(order)} />
      <p className="flex items-center gap-2 text-sm font-light text-gray-600">
        <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
        {guests > 0 ? `${guests} ${guests === 1 ? 'comensal' : 'comensales'}` : 'Comensales sin indicar'}
      </p>
      <div className="flex items-center gap-2">
        <QuantityStepper value={copies} onChange={setCopies} />
        <button
          type="button"
          onClick={() => onPrint(copies)}
          disabled={isPrinting}
          className="flex h-11 min-w-0 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-content-primary px-3 text-sm font-medium text-white transition-colors hover:bg-black disabled:opacity-60"
        >
          <Printer className="h-4 w-4 shrink-0" />
          {isPrinting ? 'Imprimiendo…' : 'Imprimir QR'}
        </button>
      </div>
    </article>
  );
}

function Heading({ name, time }: { name: string; time: string }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <h4 className="truncate text-xl font-medium text-content-primary">{name}</h4>
      <span className="ml-auto shrink-0 text-sm font-light tabular-nums text-gray-600">{time}</span>
    </div>
  );
}
